import { resolveHeaderReferences } from './sheetHeaderReferences';

const LOGIC_ACTIONS = new Set(['show', 'hide', 'enable', 'disable']);
const LOGIC_CONDITIONS = new Set(['all', 'any']);
const LOGIC_OPERATORS = new Set([
  'equals',
  'notEquals',
  'contains',
  'notContains',
  'greaterThan',
  'lessThan',
  'isEmpty',
  'isNotEmpty'
]);

const normalizeRuleValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => `${item ?? ''}`.trim())
      .filter(Boolean)
      .join(', ');
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return `${value ?? ''}`;
};

export const sanitizeHeaderConditionalLogic = (logic = null, headers = [], currentHeaderId = '') => {
  if (!logic || typeof logic !== 'object') return null;

  const validHeaderIds = new Set(
    (Array.isArray(headers) ? headers : [])
      .map((header) => `${header?.id || ''}`.trim())
      .filter((headerId) => headerId && headerId !== currentHeaderId)
  );

  const action = LOGIC_ACTIONS.has(`${logic.action || ''}`.trim()) ? `${logic.action}`.trim() : 'show';
  const condition = LOGIC_CONDITIONS.has(`${logic.condition || ''}`.trim()) ? `${logic.condition}`.trim() : 'all';
  const rules = (Array.isArray(logic.rules) ? logic.rules : [])
    .map((rule) => {
      const headerId = `${rule?.headerId || rule?.fieldId || ''}`.trim();
      const operator = LOGIC_OPERATORS.has(`${rule?.operator || ''}`.trim()) ? `${rule.operator}`.trim() : 'equals';
      if (!headerId || !validHeaderIds.has(headerId)) return null;
      return {
        headerId,
        operator,
        value: normalizeRuleValue(rule?.value)
      };
    })
    .filter(Boolean);

  if (!rules.length) return null;

  return {
    action,
    condition,
    rules
  };
};

export const evalConditionalRule = (rule, value, rowValues = {}, headers = [], currentHeaderId = '') => {
  const source = normalizeRuleValue(value);
  const target = normalizeRuleValue(resolveHeaderReferences(rule?.value, headers, rowValues, currentHeaderId));

  switch (rule?.operator) {
    case 'equals':
      return source === target;
    case 'notEquals':
      return source !== target;
    case 'contains':
      return source.toLowerCase().includes(target.toLowerCase());
    case 'notContains':
      return !source.toLowerCase().includes(target.toLowerCase());
    case 'greaterThan':
      return Number(source) > Number(target);
    case 'lessThan':
      return Number(source) < Number(target);
    case 'isEmpty':
      return source.trim() === '';
    case 'isNotEmpty':
      return source.trim() !== '';
    default:
      return false;
  }
};

export const evaluateHeaderConditionalState = (header, rowValues = {}, headers = []) => {
  const logic = header?.conditionalLogic;
  if (!logic?.rules?.length) {
    return {
      hidden: false,
      readOnly: false,
      matched: false
    };
  }

  const checks = logic.rules.map((rule) => evalConditionalRule(rule, rowValues?.[rule.headerId], rowValues, headers, header?.id));
  const matched = logic.condition === 'any' ? checks.some(Boolean) : checks.every(Boolean);

  return {
    hidden: logic.action === 'hide' ? matched : logic.action === 'show' ? !matched : false,
    readOnly: logic.action === 'disable' ? matched : logic.action === 'enable' ? !matched : false,
    matched
  };
};
